import { useEffect, useMemo, useState } from "react";

import type { User, UserRole } from "@/features/accounts/types/account.types";

import { AuthContext, type AuthContextValue } from "./auth-context";
import { useCurrentUser } from "./auth-queries";
import { isHijacked as checkHijacked } from "./hijack";
import { bootstrapSession } from "./token-store";

function deriveRole(user: User): UserRole | null {
  if (user.is_staff) return "ADMIN";
  if (user.is_employee) return "STAFF";
  if (user.account_type === "B" && user.is_manager) return "BUSINESS_MANAGER";
  if (user.account_type === "B" && !user.is_manager) return "BUSINESS_EMPLOYEE";
  if (user.account_type === "P") return "PERSONAL";
  return null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // On app boot we attempt a silent refresh against the HttpOnly refresh
  // cookie. If it succeeds we get a fresh access token in memory and
  // `useCurrentUser` fires its /me query; if not we stay anonymous and the
  // route guards bounce to /login.
  const [bootstrapped, setBootstrapped] = useState(false);

  useEffect(() => {
    bootstrapSession().then(() => setBootstrapped(true));
  }, []);

  const { data: user, isLoading } = useCurrentUser();

  const loading = !bootstrapped || isLoading;

  const value = useMemo<AuthContextValue>(() => {
    if (!user) {
      return {
        user: null,
        isLoading: loading,
        isAuthenticated: false,
        role: null,
        isAdmin: false,
        isEmployee: false,
        isCustomer: false,
        isBusinessManager: false,
        isBusinessEmployee: false,
        isPersonal: false,
        isSuperUser: false,
        isHijacked: false,
        requirePasswordReset: false,
        fullName: "",
        initials: "",
      };
    }

    const role = deriveRole(user);
    return {
      user,
      isLoading: loading,
      isAuthenticated: true,
      role,
      isAdmin: user.is_staff,
      isEmployee: user.is_employee,
      isCustomer:
        !user.is_staff &&
        !user.is_employee &&
        (user.account_type === "B" || user.account_type === "P"),
      isBusinessManager: user.is_manager && user.account_type === "B",
      isBusinessEmployee: !user.is_manager && user.account_type === "B",
      isPersonal: user.account_type === "P",
      isSuperUser: user.is_superuser,
      isHijacked: checkHijacked(),
      requirePasswordReset: user.require_password_reset,
      fullName: user.full_name || "",
      initials: `${user.first_name?.charAt(0) || ""}${user.last_name?.charAt(0) || ""}`,
    };
  }, [user, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
